/**
 * AI closeout store — Track A2.
 *
 * Backs the /closeout page. Three operations against the api-shim:
 *   generate(slug)   → POST /closeout/{slug}          — fresh closeout doc
 *   loadHistory(slug)→ GET  /closeout/{slug}/history  — past closeouts (rail)
 *   loadDetail(id)   → GET  /closeouts/{id}           — one closeout, full
 *
 * Thin fetch+cache layer — same lightweight pattern as the diagnose store.
 */
import { create } from "zustand";
import { get as apiGet, post as apiPost, ApiError } from "@/api/client";

export type SignoffReadiness = "ready" | "conditional" | "blocked";

/** The structured closeout document — mirrors closeout.py's report_closeout tool. */
export interface CloseoutReport {
  executiveSummary: string;
  deployedInventory: string;
  networkConfiguration: string;
  complianceStatus: string;
  punchList: string[];
  signoffReadiness: SignoffReadiness;
  signoffReason: string;
}

/** The raw evidence bundle the model was given — kept for auditability. */
export interface CloseoutDossier {
  site: { slug: string; code: string | null; org: string | null };
  inventory: {
    deviceCount: number;
    statusTotals: Record<string, number>;
    byClass: Record<string, Record<string, number>>;
    byModel: Record<string, number>;
    firmwares: string[];
    notOnlineDevices: unknown[];
    notOnlineTruncated: boolean;
  };
  configuration: {
    vendor?: "meraki" | "aruba" | "cli";
    vlans?: { id: number | null; name: string | null; subnet: string | null; applianceIp: string | null }[];
    enabledSsids?: { number: number | null; name: string | null; authMode: string | null }[];
    ssidSlotsTotal?: number;
    firewall?: { l3RuleCount: number; denyRules: number; allowRules: number };
    groups?: string[];
    note?: string;
  };
  compliance: unknown[];
}

/** POST /closeout/{slug} response — a fresh run. */
export interface CloseoutRun {
  id: string;
  site: { slug: string; code: string | null; org: string | null };
  result: CloseoutReport;
  dossier: CloseoutDossier;
  model: string;
  elapsedMs: number;
  usage: { inputTokens: number; outputTokens: number };
}

/** GET /closeout/{slug}/history entry — summary shape, no blobs. */
export interface CloseoutHistoryItem {
  id: string;
  createdAt: string;
  createdBy: string;
  networkId: string;
  siteSlug: string;
  siteName: string | null;
  signoffReadiness: SignoffReadiness;
  summary: string;
  modelName: string | null;
  durationMs: number | null;
}

/** GET /closeouts/{id} — a history item plus the full document + dossier. */
export interface CloseoutDetail extends CloseoutHistoryItem {
  result: CloseoutReport;
  dossier: CloseoutDossier | null;
}

function errMessage(err: unknown): string {
  if (err instanceof ApiError) {
    const detail =
      err.data && typeof err.data === "object" && "detail" in err.data
        ? String((err.data as { detail: unknown }).detail)
        : null;
    return detail ?? `API error ${err.status}`;
  }
  return err instanceof Error ? err.message : "unknown error";
}

interface State {
  generating: Record<string, boolean>;
  loadingHistory: Record<string, boolean>;
  loadingDetail: Record<string, boolean>;
  historyBySlug: Record<string, CloseoutHistoryItem[]>;
  detailById: Record<string, CloseoutDetail>;
  errorBySlug: Record<string, string>;

  generate: (slug: string, vendor?: "meraki" | "aruba" | "cli") => Promise<CloseoutRun | null>;
  loadHistory: (slug: string) => Promise<void>;
  loadDetail: (id: string) => Promise<CloseoutDetail | null>;
}

export const useCloseout = create<State>((set, get) => ({
  generating: {},
  loadingHistory: {},
  loadingDetail: {},
  historyBySlug: {},
  detailById: {},
  errorBySlug: {},

  generate: async (slug, vendor = "meraki") => {
    if (!slug || get().generating[slug]) return null;
    set((s) => ({
      generating: { ...s.generating, [slug]: true },
      errorBySlug: { ...s.errorBySlug, [slug]: "" },
    }));
    try {
      const qs = vendor && vendor !== "meraki" ? `?vendor=${encodeURIComponent(vendor)}` : "";
      const run = await apiPost<CloseoutRun>(`/closeout/${encodeURIComponent(slug)}${qs}`);
      set((s) => ({ generating: { ...s.generating, [slug]: false } }));
      void get().loadHistory(slug);
      return run;
    } catch (err) {
      set((s) => ({
        generating: { ...s.generating, [slug]: false },
        errorBySlug: { ...s.errorBySlug, [slug]: errMessage(err) },
      }));
      return null;
    }
  },

  loadHistory: async (slug) => {
    if (!slug || get().loadingHistory[slug]) return;
    set((s) => ({ loadingHistory: { ...s.loadingHistory, [slug]: true } }));
    try {
      const rows = await apiGet<CloseoutHistoryItem[]>(
        `/closeout/${encodeURIComponent(slug)}/history`,
      );
      set((s) => ({
        historyBySlug: { ...s.historyBySlug, [slug]: rows },
        loadingHistory: { ...s.loadingHistory, [slug]: false },
      }));
    } catch {
      set((s) => ({ loadingHistory: { ...s.loadingHistory, [slug]: false } }));
    }
  },

  loadDetail: async (id) => {
    if (!id) return null;
    const cached = get().detailById[id];
    if (cached) return cached;
    if (get().loadingDetail[id]) return null;
    set((s) => ({ loadingDetail: { ...s.loadingDetail, [id]: true } }));
    try {
      const detail = await apiGet<CloseoutDetail>(`/closeouts/${encodeURIComponent(id)}`);
      set((s) => ({
        detailById: { ...s.detailById, [id]: detail },
        loadingDetail: { ...s.loadingDetail, [id]: false },
      }));
      return detail;
    } catch {
      set((s) => ({ loadingDetail: { ...s.loadingDetail, [id]: false } }));
      return null;
    }
  },
}));
